import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import ResponsiveDialogActions from 'app/shared-components/layout/dialog/ResponsiveDialogActions';
import { sentryMask } from 'sentryReplayPrivacy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { createQbwcConnection, QbwcCreateResult } from 'app/services/erp-connection.service';
import QBDVerifyStep from './QBDVerifyStep';

// SEE-1042 — QuickBooks Desktop direct connect modal. Speedchain-direct (no
// Agave branding/terms), same QuickBooks Web Connector (QBWC) so the
// compatibility + install steps describe the same connector, re-pointed at our
// host. Consolidated per the 6.25 standup: the config file + credentials are
// generated automatically when the admin leaves the install step and surface
// together on a single "setup" screen (no separate Generate click), so the
// happy path is intro → compatibility → install → setup → verify.
type QbdStep = 'intro' | 'compatibility' | 'install' | 'setup' | 'verify';

const STEPS: QbdStep[] = ['intro', 'compatibility', 'install', 'setup', 'verify'];

interface QBDConnectionModalProps {
  open: boolean;
  paidolId: string;
  /** Used to name the downloaded .qwc file. */
  orgName?: string;
  onClose: () => void;
  /** Refresh the connection status on the page (verify success or already-exists). */
  onConnected: () => void;
}

// The backend returns the .qwc as XML CONTENT (not a URL); build a client-side
// download from it (Req 3.2, design §3.3).
function downloadQwc(fileContent: string, orgLabel: string) {
  const safeLabel =
    orgLabel
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'quickbooks-desktop';
  const blob = new Blob([fileContent], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `speedchain-${safeLabel}.qwc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // a.click() triggers the download asynchronously — revoking the blob URL on
  // the next tick (rather than immediately) gives the browser a moment to have
  // started consuming it first, instead of risking a race in browsers that
  // don't bind the download synchronously within click().
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function QBDConnectionModal({ open, paidolId, orgName, onClose, onConnected }: QBDConnectionModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [stepIndex, setStepIndex] = useState(0);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<QbwcCreateResult | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [verified, setVerified] = useState(false);

  const step = STEPS[stepIndex];

  const reset = () => {
    setStepIndex(0);
    setCreating(false);
    // Drop the created result — the oneTimePassword is reveal-once and must not
    // outlive the modal session (never persisted/logged). A fresh session
    // generates a fresh file + credentials (the "regenerate on return" case).
    setCreated(null);
    setCreateError(null);
    setDownloaded(false);
    setPasswordSaved(false);
    setVerified(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar(t('erpConnection.copiedToClipboard', '{{label}} copied to clipboard', { label }), {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(t('erpConnection.copyFailed', 'Failed to copy to clipboard'), { variant: 'error' });
    }
  };

  // Generate the connection EXACTLY once per session (Req 3.1) — the
  // `creating`/`created` guards make a double-click / re-entry a no-op. Returns
  // true when a connection is ready to show on the setup screen.
  const ensureConnection = async (): Promise<boolean> => {
    if (creating) return false;
    if (created) return true;
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createQbwcConnection(paidolId);
      setCreated(result);
      // Auto-download the .qwc now (still within the user's Next click, so the
      // browser allows it); the setup screen also offers a re-download button.
      downloadQwc(result.qwcFileContent, orgName ?? 'quickbooks-desktop');
      setDownloaded(true);
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      // "Already exists" is not a failure — the page only opens this modal when
      // no connection exists, so this means another tab/admin already created
      // one. Surface it on the page rather than a generic error (Req 3.4).
      if (message.includes('already exists')) {
        enqueueSnackbar(
          t(
            'erpConnection.qbd.alreadyExists',
            'A QuickBooks Desktop connection already exists for this organization.'
          ),
          { variant: 'info' }
        );
        onConnected();
        handleClose();
        return false;
      }
      // Admin-role / configuration / other errors → actionable, non-technical
      // message with retry; no raw error objects in the UI (Req 3.5).
      setCreateError(
        message.includes('administrator')
          ? t(
              'erpConnection.qbd.adminRequired',
              'You need an administrator role to connect QuickBooks Desktop.'
            )
          : t(
              'erpConnection.qbd.generateFailed',
              "Couldn't generate the connection configuration. Please try again."
            )
      );
      return false;
    } finally {
      setCreating(false);
    }
  };

  // Leaving the install step generates the config + credentials and advances to
  // the combined setup screen. Stays put (with an inline error) on failure.
  const handleInstallNext = async () => {
    const ready = await ensureConnection();
    if (ready) setStepIndex(STEPS.indexOf('setup'));
  };

  const handleReDownload = () => {
    if (!created) return;
    downloadQwc(created.qwcFileContent, orgName ?? 'quickbooks-desktop');
    setDownloaded(true);
  };

  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const stepTitle = (s: QbdStep): string => {
    switch (s) {
      case 'intro':
        return t('erpConnection.qbd.introTitle', 'Connect QuickBooks Desktop');
      case 'compatibility':
        return t('erpConnection.qbd.compatibilityTitle', 'Check compatibility');
      case 'install':
        return t('erpConnection.qbd.installTitle', 'Install the QuickBooks Web Connector');
      case 'setup':
        return t('erpConnection.qbd.setupTitle', 'Add the connector and save your password');
      case 'verify':
        return t('erpConnection.qbd.verifyTitle', 'Verify the connection');
      default:
        return '';
    }
  };

  const renderStep = () => {
    switch (step) {
      case 'intro':
        return (
          <Typography variant="body1">
            {t(
              'erpConnection.qbd.introBody',
              'Speedchain connects directly to your QuickBooks Desktop using the QuickBooks Web Connector. This guide walks you through checking compatibility, installing the connector, and verifying your first sync. It takes about five minutes.'
            )}
          </Typography>
        );
      case 'compatibility':
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t(
                'erpConnection.qbd.compatibilityBody',
                'Before you start, confirm your setup meets the QuickBooks Web Connector requirements:'
              )}
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                <ListItemText
                  primary={t(
                    'erpConnection.qbd.compatibilityWindows',
                    'QuickBooks Desktop is running on a supported Windows machine (Windows 10 or later).'
                  )}
                />
              </ListItem>
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                <ListItemText
                  primary={t(
                    'erpConnection.qbd.compatibilityVersion',
                    'You are on a supported QuickBooks Desktop version (Pro, Premier, or Enterprise, 2019 or later).'
                  )}
                />
              </ListItem>
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                <ListItemText
                  primary={t(
                    'erpConnection.qbd.compatibilityAdmin',
                    'You can open your company file as the QuickBooks Admin user in single-user mode.'
                  )}
                />
              </ListItem>
            </List>
          </Box>
        );
      case 'install':
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t(
                'erpConnection.qbd.installBody',
                'Install the QuickBooks Web Connector, then prepare QuickBooks to accept the configuration file:'
              )}
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
                <ListItemText
                  primary={t(
                    'erpConnection.qbd.installStep1',
                    'Download and install the QuickBooks Web Connector from Intuit if it is not already installed.'
                  )}
                />
              </ListItem>
              <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
                <ListItemText
                  primary={t(
                    'erpConnection.qbd.installStep2',
                    'Open your company file in QuickBooks Desktop as the Admin user.'
                  )}
                />
              </ListItem>
              <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', pl: 0 }}>
                <ListItemText
                  primary={t(
                    'erpConnection.qbd.installStep3',
                    'Go to File → App Management → Update Web Services to open the Web Connector.'
                  )}
                />
              </ListItem>
            </List>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t(
                'erpConnection.qbd.installNextHint',
                'When you click Next we generate your configuration file and one-time password.'
              )}
            </Typography>
            {createError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {createError}
              </Alert>
            )}
          </Box>
        );
      case 'setup':
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t(
                'erpConnection.qbd.setupBody',
                'Your configuration file has downloaded. In the Web Connector, click "Add an Application" and select it, then paste the one-time password below when prompted.'
              )}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleReDownload}
              disabled={!created}
            >
              {t('erpConnection.qbd.downloadButton', 'Download .qwc')}
            </Button>
            {downloaded && (
              <Typography variant="body2" color="success.main" sx={{ mt: 1.5 }}>
                {t('erpConnection.qbd.downloadDone', 'Downloaded. Add it to the Web Connector to continue.')}
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            <Alert severity="warning" sx={{ mb: 3 }}>
              {t(
                'erpConnection.qbd.passwordWarning',
                'Copy this password now. For security it is shown only once and cannot be retrieved later. The Web Connector will prompt you for it the first time it runs.'
              )}
            </Alert>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              {t('erpConnection.qbd.username', 'Web Connector username')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TextField
                value={created?.qbwcUsername ?? ''}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
              <IconButton
                onClick={() => handleCopy(created?.qbwcUsername ?? '', 'Username')}
                aria-label={t('erpConnection.qbd.copyUsername', 'Copy username')}
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              {t('erpConnection.qbd.password', 'One-time password')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <TextField
                value={created?.oneTimePassword ?? ''}
                fullWidth
                size="small"
                // Reveal-once secret shown for the user to copy into QBWC — keep it
                // visible (readOnly, not type="password"), but stop browsers from
                // autofilling/caching it via saved-password suggestions.
                autoComplete="off"
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
              <IconButton
                onClick={() => handleCopy(created?.oneTimePassword ?? '', 'Password')}
                aria-label={t('erpConnection.qbd.copyPassword', 'Copy password')}
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
            <FormControlLabel
              control={
                <Checkbox checked={passwordSaved} onChange={(e) => setPasswordSaved(e.target.checked)} />
              }
              label={
                <Typography variant="body2">
                  {t('erpConnection.qbd.passwordAck', 'I have saved this password in a secure location')}
                </Typography>
              }
            />
          </Box>
        );
      case 'verify':
        return <QBDVerifyStep paidolId={paidolId} onSuccess={() => setVerified(true)} />;
      default:
        return null;
    }
  };

  // The footer primary action varies per step.
  const renderPrimaryAction = () => {
    switch (step) {
      case 'install':
        return (
          <Button
            variant="contained"
            onClick={handleInstallNext}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {creating ? t('erpConnection.qbd.generating', 'Generating…') : t('next', 'Next')}
          </Button>
        );
      case 'setup':
        return (
          <Button variant="contained" onClick={goNext} disabled={!downloaded || !passwordSaved}>
            {t('next', 'Next')}
          </Button>
        );
      case 'verify':
        return (
          <Button variant="contained" onClick={handleClose} disabled={!verified}>
            {t('finish', 'Finish')}
          </Button>
        );
      default:
        return (
          <Button variant="contained" onClick={goNext}>
            {t('next', 'Next')}
          </Button>
        );
    }
  };

  return (
    <ResponsiveDialog
      {...sentryMask}
      open={open}
      // Block close-on-backdrop/escape while a create call is in flight (never
      // orphan a connection mid-create) AND on the setup step — dismissing there
      // without copying the one-time password forces regenerating a new one, so
      // we require the explicit Cancel button below. (SEE-1042, Travis)
      onClose={() => {
        if (!creating && step !== 'setup') handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('erpConnection.qbd.stepCount', 'Step {{current}} of {{total}}', {
            current: stepIndex + 1,
            total: STEPS.length,
          })}
        </Typography>
        {stepTitle(step)}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>{renderStep()}</Box>
      </DialogContent>
      <ResponsiveDialogActions>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          {step === 'setup' ? (
            // Click-outside is blocked on this step, so give an explicit exit.
            // (SEE-1042, Travis)
            <Button onClick={handleClose} sx={{ color: theme.palette.text.secondary }}>
              {t('cancel', 'Cancel')}
            </Button>
          ) : (
            <Button
              onClick={goBack}
              disabled={stepIndex === 0 || creating}
              sx={{ color: theme.palette.text.secondary }}
            >
              {t('back', 'Back')}
            </Button>
          )}
          {renderPrimaryAction()}
        </Box>
      </ResponsiveDialogActions>
    </ResponsiveDialog>
  );
}

export default QBDConnectionModal;
